import { Head, Link, router } from '@inertiajs/react';
import React, { useState, useRef } from 'react';
import { SimpleFileUpload } from 'simple-file-upload-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Copy, X } from "lucide-react";
import { toast } from "sonner";

export default function Index({ bucket }) {
  const [tag, setTag] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [processedFiles, setProcessedFiles] = useState({}); // Track processed versions by original file ID
  const [isProcessing, setIsProcessing] = useState({}); // Track processing state by file ID
  const fileUploadRef = useRef(null);

  const handleFileChange = (event) => {
    
    // Handle successful uploads
    if (event.allFiles && event.allFiles.length > 0) {
      // For single file upload, just take the first file
      const file = event.allFiles[0];
      const newFile = {
        id: file.id || file.name,
        file_name: file.name,
        file_size: file.size < 1048576 ? file.size / 1024 : file.size / 1048576, // Convert to KB or MB
        cdn_url: file.cdnUrl || file.preview,
        content_type: file.type || 'application/octet-stream',
        tag: tag || "No tag",
        created_at: new Date().toISOString()
      };
      
      // Replace with single file
      setUploadedFiles([newFile]);
      toast.success('File uploaded successfully!');
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('URL copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleRemoveBackground = async (file) => {
    // Set processing state
    setIsProcessing(prev => ({ ...prev, [file.id]: true }));
    
    router.post('/background_removals', 
      { url: file.cdn_url },
      {
        preserveState: true,
        preserveScroll: true,
        preserveUrl: true, // This prevents URL change
        only: ['flash', 'background_removed_url', 'processed_image_id', 'original_url'], // Only update these props

        onSuccess: (page) => {
          // Check if we have success data in props
          if (page.props.flash?.success && page.props.background_removed_url) {
            toast.success('Background removed successfully!');
            
            // Create new processed file object
            const processedFile = {
              id: page.props.processed_image_id,
              file_name: file.file_name.replace(/\.(png|jpg|jpeg)$/i, '_no_bg.png'),
              cdn_url: page.props.background_removed_url,
              content_type: 'image/png',
              original_url: page.props.original_url
            };
            
            setProcessedFiles(prev => ({
              ...prev,
              [file.id]: processedFile
            }));
          }
          
          // Clear processing state
          setIsProcessing(prev => ({ ...prev, [file.id]: false }));
        },
        onError: (errors) => {
          toast.error('Failed to remove background');
          console.error('Background removal error:', errors);
          
          // Clear processing state
          setIsProcessing(prev => ({ ...prev, [file.id]: false }));
        }
      }
    );
  };

  return (
    <div className="min-h-screen">
      <Head title="Edit Images" />
      
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Remove the Background From Your Images</h1>
          <p className="text-xl text-gray-600 mb-6">
            Remove backgrounds from your images with just one click.<br />
            Upload a new image or select from your existing files—it's kind of like magic ✨
          </p>
          
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl">
            <p className="text-sm font-medium text-blue-900 mb-2">How it works:</p>
            <ul className="space-y-1.5 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>Process one image at a time for best results</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>Basic plan includes 20 background removals per month</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>Processed images are automatically tagged with "removed-bg"</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5">•</span>
                <span>All images are saved to your <Link href="/dashboard" className="text-blue-600 hover:text-blue-800 underline font-medium">Dashboard</Link></span>
              </li>
            </ul>
          </div>
        </div>

        {/* Main Layout */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload New Image</h2>
              
              <div className="space-y-4">
                {/* Tag Input */}
                <div>
                  <Label htmlFor="tag" className="text-sm font-medium text-gray-700 mb-1.5 block">
                    Tag
                  </Label>
                  <Input
                    id="tag"
                    type="text"
                    placeholder="e.g., landing-page"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Optional: Organize with tags
                  </p>
                </div>

                {/* Uploader Component */}
                <div className="pt-4 w-full max-w-full [&_*]:outline-none [&_*]:border-none [&_*:focus]:outline-none [&_*:focus]:border-none [&_*:focus]:ring-0 [&_*:focus-visible]:outline-none [&_*:focus-visible]:ring-0 [&_*:active]:outline-none [&_*:active]:ring-0">
                  <SimpleFileUpload
                    ref={fileUploadRef}
                    // publicKey = "e8557605f1b5ac9b18c913603d29a8c8"
                    publicKey={bucket.public_key}
                    onChange={handleFileChange}
                    multiple={false}
                    maxFiles={1}
                    tag={tag || undefined}
                  />
                </div>
              </div>
            </div>
            
            {/* Files Section */}
            <div className="mt-6">
              {/* Files Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Your Files {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
                  </h2>
                  {uploadedFiles.length > 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      {uploadedFiles[0].file_name}
                    </p>
                  )}
                </div>
              </div>

            {/* Empty State */}
            {uploadedFiles.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
                <div className="text-gray-400 mb-4">
                  <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No files uploaded yet</h3>
                <p className="text-gray-500">Upload a file to get started</p>
              </div>
            ) : (
              /* Side-by-Side Image Display */
              <div className="space-y-6 w-full min-w-0">
                {uploadedFiles.map((file) => {
                  const processedFile = processedFiles[file.id];
                  const isProcessingFile = isProcessing[file.id];
                  
                  return (
                    <div key={file.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                      {/* File Info Header */}
                      <div className="p-6 border-b border-gray-100">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-lg font-semibold text-gray-900 truncate" title={file.file_name}>
                              {file.file_name}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {file.file_size < 1 ? `${(file.file_size * 1024).toFixed(0)} KB` : `${file.file_size.toFixed(1)} MB`}
                            </p>
                          </div>
                          {file.tag && file.tag !== "No tag" && (
                            <Badge variant="outline" className="text-sm shrink-0">
                              {file.tag}
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Images Section */}
                      {file.content_type?.startsWith('image/') ? (
                        <div className="p-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Original Image */}
                            <div className="space-y-3">
                              <h3 className="text-sm font-medium text-gray-700">Original</h3>
                              <div className="relative aspect-square bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                                <img 
                                  src={file.cdn_url} 
                                  alt={file.file_name}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                              <p className="text-xs text-gray-500 text-center truncate" title={file.file_name}>
                                {file.file_name}
                              </p>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => copyToClipboard(file.cdn_url)}
                                className="w-full"
                              >
                                <Copy className="w-4 h-4 mr-1.5" />
                                Copy Original URL
                              </Button>
                            </div>

                            {/* Processed Image or Action */}
                            <div className="space-y-3">
                              <h3 className="text-sm font-medium text-gray-700">Background Removed</h3>
                              <div className="relative aspect-square bg-gray-50 rounded-lg overflow-hidden border border-gray-200">
                                {processedFile ? (
                                  <img 
                                    src={processedFile.cdn_url} 
                                    alt={processedFile.file_name}
                                    className="w-full h-full object-contain"
                                  />
                                ) : isProcessingFile ? (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <div className="text-center">
                                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                                      <p className="text-sm text-gray-500">Processing...</p>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <div className="text-center">
                                      <div className="text-4xl mb-3">✨</div>
                                      <p className="text-sm text-gray-500">Click to remove background</p>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              {processedFile ? (
                                <>
                                  <p className="text-xs text-gray-500 text-center truncate" title={processedFile.file_name}>
                                    {processedFile.file_name}
                                  </p>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => copyToClipboard(processedFile.cdn_url)}
                                    className="w-full"
                                  >
                                    <Copy className="w-4 h-4 mr-1.5" />
                                    Copy Processed URL
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <p className="text-xs text-gray-400 text-center">
                                    {file.file_name.replace(/\.(png|jpg|jpeg)$/i, '_no_bg.png')}
                                  </p>
                                  <Button
                                    size="sm"
                                    onClick={() => handleRemoveBackground(file)}
                                    disabled={isProcessingFile}
                                    className="w-full"
                                  >
                                    {isProcessingFile ? (
                                      <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Processing...
                                      </>
                                    ) : (
                                      <>
                                        <X className="w-4 h-4 mr-1.5" />
                                        Remove Background
                                      </>
                                    )}
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Non-image files */
                        <div className="p-6">
                          <div className="bg-gray-50 rounded-lg p-8 text-center">
                            <div className="text-5xl mb-2">📄</div>
                            <p className="text-sm text-gray-500 font-medium">Document</p>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(file.cdn_url)}
                              className="mt-4"
                            >
                              <Copy className="w-4 h-4 mr-1.5" />
                              Copy URL
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </div>

          {/* Right Column - Available for future content */}
          <div className="min-w-0 overflow-hidden">
            {/* This column is now available for other content */}
          </div>
        </div>
      </div>
    </div>
  );
}
